import { useQuery } from "@tanstack/react-query";
import { LineChart, TrendingUp, TrendingDown, Clock, Target, Zap } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface BatchSession {
  id: string;
  sessionId: string;
  sprintFilter: string | null;
  startedAt: string;
  completedAt: string | null;
  totalIssues: number;
  issuesPassed: number;
  issuesFailed: number;
  firstTimeRight: number;
  firstTimeRightRate: string | null;
  reworkRate: string | null;
  totalDurationMin: string | null;
  avgTimePerIssueMin: string | null;
  issuesPerHour: string | null;
  designReviewPassRate: string | null;
  totalTestsPassed: number;
  totalTestsFailed: number;
  testingLevel: string | null;
}

interface BatchMetrics {
  totalBatches: number;
  recentBatches: BatchSession[];
  avgFirstTimeRightRate: number | null;
  avgReworkRate: number | null;
  avgTimePerIssue: number | null;
  avgDesignReviewPassRate: number | null;
  trends: Array<{
    date: string;
    firstTimeRightRate: number | null;
    reworkRate: number | null;
    avgTimePerIssue: number | null;
  }>;
}

export function BatchMetricsCard() {
  const { data: metrics, isLoading } = useQuery<BatchMetrics>({
    queryKey: ["/api/crm/batch-sessions/metrics"],
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Don't render if no batch data exists
  if (!isLoading && (!metrics || metrics.totalBatches === 0)) {
    return null;
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5" />
            Batch Performance
          </CardTitle>
          <CardDescription>
            Metrics from batch-fix workflow executions
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-40 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const hasData = metrics && metrics.totalBatches > 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Zap className="h-5 w-5" />
          Batch Performance
        </CardTitle>
        <CardDescription>
          Metrics from {metrics?.totalBatches ?? 0} batch-fix workflow executions
        </CardDescription>
      </CardHeader>
      <CardContent>
        {hasData ? (
          <div className="space-y-6">
            {/* Summary Stats */}
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div className="flex items-start gap-3">
                <div className="rounded-lg bg-green-500/10 p-2">
                  <Target className="h-4 w-4 text-green-600 dark:text-green-400" />
                </div>
                <div className="flex-1 space-y-1">
                  <p className="text-sm text-muted-foreground">Avg First-Time-Right</p>
                  <p className="text-2xl font-bold">
                    {metrics.avgFirstTimeRightRate != null
                      ? `${metrics.avgFirstTimeRightRate.toFixed(1)}%`
                      : "N/A"}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="rounded-lg bg-yellow-500/10 p-2">
                  <TrendingDown className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
                </div>
                <div className="flex-1 space-y-1">
                  <p className="text-sm text-muted-foreground">Avg Rework Rate</p>
                  <p className="text-2xl font-bold">
                    {metrics.avgReworkRate != null
                      ? `${metrics.avgReworkRate.toFixed(1)}%`
                      : "N/A"}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="rounded-lg bg-blue-500/10 p-2">
                  <Clock className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="flex-1 space-y-1">
                  <p className="text-sm text-muted-foreground">Avg Time/Issue</p>
                  <p className="text-2xl font-bold">
                    {metrics.avgTimePerIssue != null
                      ? `${metrics.avgTimePerIssue.toFixed(1)}m`
                      : "N/A"}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="rounded-lg bg-purple-500/10 p-2">
                  <TrendingUp className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                </div>
                <div className="flex-1 space-y-1">
                  <p className="text-sm text-muted-foreground">Design Review Pass</p>
                  <p className="text-2xl font-bold">
                    {metrics.avgDesignReviewPassRate != null
                      ? `${metrics.avgDesignReviewPassRate.toFixed(1)}%`
                      : "N/A"}
                  </p>
                </div>
              </div>
            </div>

            {/* Recent Batches Table */}
            <div>
              <h3 className="text-sm font-semibold mb-3">Recent Batch Sessions</h3>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[180px]">Session ID</TableHead>
                      <TableHead className="w-[100px]">Sprint</TableHead>
                      <TableHead className="w-[80px] text-right">Issues</TableHead>
                      <TableHead className="w-[100px] text-right">FTR Rate</TableHead>
                      <TableHead className="w-[100px] text-right">Avg Time</TableHead>
                      <TableHead className="w-[120px]">Testing Level</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {metrics.recentBatches.slice(0, 5).map((batch) => (
                      <TableRow key={batch.id}>
                        <TableCell className="font-mono text-xs">
                          {batch.sessionId}
                        </TableCell>
                        <TableCell className="text-sm">
                          {batch.sprintFilter || "ALL"}
                        </TableCell>
                        <TableCell className="text-right">
                          {batch.totalIssues}
                        </TableCell>
                        <TableCell className="text-right">
                          {batch.firstTimeRightRate
                            ? `${parseFloat(batch.firstTimeRightRate).toFixed(0)}%`
                            : "N/A"}
                        </TableCell>
                        <TableCell className="text-right">
                          {batch.avgTimePerIssueMin
                            ? `${parseFloat(batch.avgTimePerIssueMin).toFixed(1)}m`
                            : "N/A"}
                        </TableCell>
                        <TableCell>
                          <span className="inline-flex items-center rounded-full px-2 py-1 text-xs font-medium bg-blue-500/10 text-blue-700 dark:text-blue-300">
                            {batch.testingLevel || "N/A"}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* Trend Indicator */}
            {metrics.trends.length >= 2 && (
              <div className="rounded-lg bg-muted/50 p-4">
                <div className="flex items-center gap-2 text-sm">
                  <LineChart className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">
                    Trend over last {metrics.trends.length} batches:
                  </span>
                  {metrics.trends[metrics.trends.length - 1].firstTimeRightRate != null &&
                   metrics.trends[0].firstTimeRightRate != null ? (
                    <span
                      className={
                        metrics.trends[metrics.trends.length - 1].firstTimeRightRate! >
                        metrics.trends[0].firstTimeRightRate!
                          ? "text-green-600 dark:text-green-400 font-medium"
                          : "text-yellow-600 dark:text-yellow-400 font-medium"
                      }
                    >
                      {metrics.trends[metrics.trends.length - 1].firstTimeRightRate! >
                      metrics.trends[0].firstTimeRightRate!
                        ? "↗ Improving"
                        : "↘ Declining"}
                    </span>
                  ) : (
                    <span className="text-muted-foreground font-medium">
                      Insufficient data
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Zap className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-sm text-muted-foreground max-w-md">
              No batch sessions have been executed yet. Run /batch-fix to generate
              batch performance metrics.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
